/**
 * PopupScopeBar — 运营弹窗（ADM-OPS-S007）专属的 App 层选择条。
 *
 * 为什么不复用 `OpsScopeBar`：那条 bar 的形状是「语言必填 × App 可选覆盖」两轴，还挂着 §0.4
 * 「从全局复制」/ §0.5「移除覆盖」。运营弹窗**没有语言维度**，且 dev-docs
 * `02-API契约/A2-运营配置.md` §5 头部明写本模块「不进 §85 维度模型、不建 `app_dedicated_configs`
 * 身份行、不接受 §0.4 复制与 §0.5 移除」。复用就得把语言行、note 文案、复制/移除三处全 gate 掉，
 * 反而比单独一条更难读，也更容易在后续改动里漏 gate 把语言轴放回来。
 *
 * 视觉沿用 OpsScopeBar 的既有形制（同 `card` + `btn btn-sm` chip 组 + 同 note 排版），只是去掉语言
 * 那一行——不是新设计决策，故不走设计路径。
 */

import type { OpsConfigSource } from '@novel-admin/shared';
import { useApps } from '~/data/queries';
import type { PopupLayerAppBrief } from '~/data/use-ops-shelf-popup';
import { isOpsGlobal } from './ops-scope';

/** config_source → 状态 chip（判据是「该 App 的未软删行数」，不是身份行，§5.1）*/
const SOURCE_META: Record<OpsConfigSource, { label: string; cls: string }> = {
  GLOBAL: { label: '全局层', cls: 'chip-info' },
  GLOBAL_INHERITED: { label: '无覆盖 · 读全局层', cls: 'chip-warn' },
  APP_DEDICATED: { label: '已覆盖', cls: 'chip-pos' },
};

export function PopupScopeBar({
  appId,
  configSource,
  readers,
  covered,
  onApp,
}: {
  appId: string;
  /** null = 取数未就绪：显示中性态，**不向运营断言层状态** */
  configSource: OpsConfigSource | null;
  /**
   * 当前真读全局层的 App（零覆盖行）。只在全局层视图有意义；`null` = 未就绪 / 有失败，
   * 此时不渲染任何计数 —— 把「还没查到」画成「0 个 App 读取」会让运营据假事实决策。
   */
  readers: PopupLayerAppBrief[] | null;
  /** 已有自己覆盖层、不读全局层的 App；与 `readers` 同生同灭 */
  covered: PopupLayerAppBrief[] | null;
  onApp: (id: string) => void;
}) {
  const { data: apps } = useApps();
  const meta = configSource ? SOURCE_META[configSource] : { label: '加载中…', cls: 'chip-warn' };
  /** 零读者时补一句「谁把自己隔离出去了」，运营才知道该去哪个 App 层改。 */
  const coveredNote =
    covered && covered.length > 0
      ? `（${covered.map((c) => c.name).join('、')} 均已有自己的覆盖层）`
      : '（当前无在营 App）';

  return (
    <div className="card" style={{ padding: '10px 14px 12px' }}>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="label" style={{ width: 30 }}>
          App
        </span>
        <button
          type="button"
          className={`btn btn-sm ${isOpsGlobal(appId) ? 'btn-accent' : ''}`}
          title="全局层（app_id 空）：没有自己覆盖层的 App 全读这一份"
          onClick={() => onApp('all')}
        >
          全部
        </button>
        {apps.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`btn btn-sm ${a.id === appId ? 'btn-accent' : ''}`}
            title="该 App 的覆盖层"
            onClick={() => onApp(a.id)}
          >
            {a.name}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {configSource === 'GLOBAL' && readers !== null && (
          <span
            className={`chip ${readers.length === 0 ? 'chip-warn' : 'chip-info'}`}
            style={{ fontSize: 10 }}
            title="全局层只对没有自己覆盖层的 App 生效；这里是当前真读这一层的 App 数"
          >
            {readers.length === 0 ? '当前 0 个 App 读取' : `${readers.length} 个 App 读取`}
          </span>
        )}
        {/* 层状态 chip 带稳定标识：它左边可能还有读者计数 chip，两者 class 同族（chip-info /
            chip-warn），只按 class 选会取到先出现的那个 —— 断言就会悄悄改成在验另一个元素。 */}
        <span
          className={`chip ${meta.cls}`}
          style={{ fontSize: 10 }}
          data-testid="popup-layer-chip"
        >
          {meta.label}
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 9, lineHeight: 1.6 }}>
        {configSource === null ? (
          <>层状态未知 —— 正在读取或读取失败；刷新后重试。</>
        ) : configSource === 'GLOBAL' ? (
          <>
            「全部」= <span className="mono">app_id</span> 空的
            <span style={{ fontWeight: 500 }}>全局层</span>：没有自己覆盖层的 App 全读这一份，
            不是各 App 聚合。
            {readers === null ? (
              <> 正在核对当前有哪些 App 读这一层…</>
            ) : readers.length > 0 ? (
              <>
                当前读这一层的 App：<b>{readers.length} 个</b>（
                {readers.map((r) => r.name).join('、')}）—— 在此上下架对它们同时生效。
              </>
            ) : (
              <>
                <b>当前读这一层的 App：0 个</b>
                {coveredNote}。此处改动<b>不影响任何线上 App</b>，仅作为
                <b>新建 App 的默认值</b> —— 新 App 建出来是零覆盖行，会直接读这一份。
              </>
            )}
          </>
        ) : configSource === 'GLOBAL_INHERITED' ? (
          <>
            该 App <span style={{ fontWeight: 500 }}>没有覆盖层</span>：C 端读全局层那一份，
            本表不列全局行。在此新建任一场景即转为该 App 独立一层
            <b>（此后不再回落全局层，且服务端禁止删到零行）</b>。
          </>
        ) : (
          <>
            该 App <span style={{ fontWeight: 500 }}>已有覆盖层</span>：C 端只读这一层，
            不与全局层并集。<span className="mono">语言</span>不参与选取 —— 本层对该 App
            全部语言一致。
          </>
        )}
      </div>
    </div>
  );
}
